import { useCallback, useEffect, useState, useRef } from 'react';
import { getTransactions } from '../../../../auth/_requests';
import { particularTransaction } from '../../../utils/types';
import {
  transformTransactionData,
  TransactionData,
  withExpectedPaymentSendDate,
} from '../utils/transactionUtils';

interface UseTransactionsProps {
  authToken: string | undefined;
  tenantTransactionId?: string | null;
}

interface UseTransactionsReturn {
  transactions: TransactionData[];
  isLoading: boolean;
  leftSideRef: React.RefObject<HTMLDivElement>;
  refetch: () => Promise<void>;
}

const LIMIT = 10;

export const useTransactions = ({
  authToken,
  //tenantTransactionId,
}: UseTransactionsProps): UseTransactionsReturn => {
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const leftSideRef = useRef<HTMLDivElement>(null);

  const refetch = useCallback(async () => {
    if (!authToken) return;
    setCurrentPage(1);
    setIsLoading(true);
    try {
      const response = await getTransactions(authToken, { page: 1, limit: LIMIT });
      const transactionsData = await withExpectedPaymentSendDate(
        authToken,
        response.data.map((item: particularTransaction) => transformTransactionData(item))
      );
      setTransactions(transactionsData);
      setTotalCount(response.meta.totalCount);
    } catch (error) {
      console.error('Error fetching tenant transactions', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!authToken) return;

      setIsLoading(true);
      try {
        const response = await getTransactions(authToken, { page: currentPage, limit: LIMIT });
        const transactionsData = await withExpectedPaymentSendDate(
          authToken,
          response.data.map((item: particularTransaction) => transformTransactionData(item))
        );

        setTransactions((prev) =>
          currentPage === 1 ? transactionsData : [...prev, ...transactionsData]
        );
        setTotalCount(response.meta.totalCount);
      } catch (error) {
        console.error('Error fetching tenant transactions', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchTransactions();
  }, [authToken, currentPage]);

  useEffect(() => {
    const leftSide = leftSideRef.current;
    if (!leftSide) return;

    const handleScroll = () => {
      if (
        leftSide.scrollTop + leftSide.clientHeight >= leftSide.scrollHeight - 50 &&
        !isLoading &&
        transactions.length < totalCount
      ) {
        setCurrentPage((prev) => prev + 1);
      }
    };

    leftSide.addEventListener('scroll', handleScroll);
    return () => leftSide.removeEventListener('scroll', handleScroll);
  }, [isLoading, transactions.length, totalCount]);

  return {
    transactions,
    isLoading,
    leftSideRef,
    refetch,
  };
};

